import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import OrderDetailLayoutHeader from "./OrderDetailLayoutHeader.jsx"
import OrderPage from "./OrderPage.jsx"
import Toast from "../common/Toast.jsx"
import useNotImplementedToast from "../hooks/useNotImplementedToast.js"
import CustomerDetailsSlideout from "../customers/CustomerDetailsSlideout.jsx"
import CreateEditCustomerModal from "../customers/CreateEditCustomerModal.jsx"

function getHeaderContact(order) {
    if (!order) {
        return {}
    }
    return order.bill_same_as_ship || !order.bill_to ? order.ship_to ?? {} : order.bill_to ?? {}
}

/**
 * @param {{
 *   app: any,
 *   api: any,
 *   helpers: any,
 *   activeTab?: string,
 *   showWeighInNotes?: boolean,
 *   showBoxUpTable?: boolean,
 *   showShipmentNotes?: boolean,
 *   showCreateSoButton?: boolean,
 *   showOrderNav?: boolean,
 *   orderInformationFieldLayout?: any,
 *   lockEditsWhenConfirmed?: boolean,
 *   editLockedToastMessage?: string,
 *   renderOrderStatusActions?: (ctx: {
 *     Button: any,
 *     order: any,
 *     app: any,
 *     api: any,
 *     helpers: any,
 *     refreshOrder: () => Promise<void>,
 *     onError: (error: any) => void,
 *     onSuccess: (message: string) => void,
 *   }) => React.ReactNode,
 *   renderOrderFooterExtra?: (ctx: {
 *     Button: any,
 *     order: any,
 *     onOrderChange: (order: any) => void,
 *   }) => React.ReactNode,
 *   renderOrderAlerts?: (ctx: { order: any }) => React.ReactNode,
 *   customerSlideoutProps?: {
 *     enableStatus?: boolean,
 *     enableCommunication?: boolean,
 *     enableTypeBadges?: boolean,
 *     enableLodge?: boolean,
 *     ordersTableYearParam?: string,
 *   },
 *   injectBootstrap?: boolean,
 * }} props
 */
export default function OrderLayoutPage({
    app,
    api,
    helpers,
    activeTab = "order",
    showWeighInNotes = true,
    showBoxUpTable = true,
    showShipmentNotes = true,
    showCreateSoButton = true,
    showOrderNav = true,
    orderInformationFieldLayout = undefined,
    lockEditsWhenConfirmed = false,
    editLockedToastMessage = undefined,
    renderOrderStatusActions = undefined,
    renderOrderFooterExtra = undefined,
    renderOrderAlerts = undefined,
    customerSlideoutProps = undefined,
    injectBootstrap = false,
    disableManifestEditWhenOffline = false,
}) {
    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()
    const { toastProps, showNotImplementedToast } = useNotImplementedToast()
    const orderState = location.state?.order ?? {}
    const [order, setOrder] = useState(null)
    const [manifest, setManifest] = useState(null)
    const [boxUpLines, setBoxUpLines] = useState([])
    const [boxUpBoxes, setBoxUpBoxes] = useState([])
    const [tidepoolLoading, setTidepoolLoading] = useState(false)
    const [isCustomerSlideoutOpen, setIsCustomerSlideoutOpen] = useState(false)
    const [slideoutCustomer, setSlideoutCustomer] = useState(null)
    const [isCustomerDetailsLoading, setIsCustomerDetailsLoading] = useState(false)
    const [showCustomerModal, setShowCustomerModal] = useState(false)

    const MIN_CUSTOMER_DETAILS_SPINNER_MS = 300

    useEffect(() => {
        let cancelled = false

        async function fetchOrder() {
            try {
                let fetchedOrder = null
                if (orderState.id) {
                    fetchedOrder = await api.getOrder(orderState.id)
                } else {
                    if (/^\d+$/.test(params.orderId ?? "")) {
                        fetchedOrder = await api.getOrder(Number(params.orderId))
                    } else {
                        // Back-compat: older links used master in the route param.
                        fetchedOrder = await api.getOrderByMaster(params.orderId)
                    }
                }

                if (!cancelled) {
                    setOrder(fetchedOrder)
                }
            } catch (error) {
                console.error("Failed to load v2 order details:", error)
            }
        }

        fetchOrder()
        return () => {
            cancelled = true
        }
    }, [orderState.id, params.orderId])

    useEffect(() => {
        if (!order?.manifest_id) {
            setManifest(null)
            return
        }
        let cancelled = false
        api.getManifest(order.manifest_id)
            .then((fetchedManifest) => {
                if (!cancelled) {
                    setManifest(fetchedManifest ?? null)
                }
            })
            .catch((error) => {
                console.error("Failed to load v2 order manifest:", error)
                if (!cancelled) {
                    setManifest(null)
                }
            })
        return () => {
            cancelled = true
        }
    }, [order?.manifest_id])

    useEffect(() => {
        if (!order?.id || activeTab !== "order" || !showBoxUpTable) {
            if (!showBoxUpTable) {
                setBoxUpLines([])
                setBoxUpBoxes([])
                setTidepoolLoading(false)
            }
            return
        }

        setBoxUpLines(order.box_up_lines ?? [])
        setBoxUpBoxes(order.box_up_boxes ?? [])

        let cancelled = false
        setTidepoolLoading(true)
        const getTidepool = api.getOrderTidepool ?? (async () => ({ boxes: [] }))
        getTidepool(order.id)
            .then((info) => {
                if (cancelled) {
                    return
                }
                const merge = helpers?.mergeTidepoolOntoBoxes
                setBoxUpBoxes(merge ? merge(order.box_up_boxes ?? [], info?.boxes) : (order.box_up_boxes ?? []))
            })
            .catch((error) => {
                console.error("Failed to load Tidepool order info:", error)
                if (!cancelled) {
                    setBoxUpBoxes(order.box_up_boxes ?? [])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setTidepoolLoading(false)
                }
            })
        return () => {
            cancelled = true
        }
    }, [order?.id, order?.box_up_lines, order?.box_up_boxes, activeTab, showBoxUpTable, api, helpers])

    const headerContact = getHeaderContact(order)
    const orderMaster = order?.master || orderState.master || params.orderId || ""
    const firstName = headerContact.first_name || orderState.firstName || ""
    const lastName = headerContact.last_name || orderState.lastName || ""

    return (
        <div className="bg-white min-vh-100 w-100" style={{ minWidth: 0 }}>
            <Toast {...toastProps} />
            <div className="w-100" style={{ minWidth: 0 }}>
                <OrderDetailLayoutHeader
                    orderMaster={orderMaster}
                    firstName={firstName}
                    lastName={lastName}
                    activeTab={activeTab}
                    orderId={params.orderId ?? ""}
                    showOrderNav={showOrderNav}
                    onNotImplemented={() => showNotImplementedToast()}
                    isCustomerDetailsLoading={isCustomerDetailsLoading}
                    alertsSlot={typeof renderOrderAlerts === "function" ? renderOrderAlerts({ order }) : null}
                    onCustomerDetailsClick={async () => {
                        if (isCustomerDetailsLoading) {
                            return
                        }
                        if (isCustomerSlideoutOpen) {
                            setIsCustomerSlideoutOpen(false)
                            return
                        }

                        const customerId = order?.customer_id
                        if (!customerId) {
                            setSlideoutCustomer(null)
                            setIsCustomerSlideoutOpen(true)
                            return
                        }

                        if (slideoutCustomer?.id === customerId) {
                            setIsCustomerSlideoutOpen(true)
                            return
                        }

                        const startedAtMs = Date.now()
                        setIsCustomerDetailsLoading(true)
                        try {
                            const customer = await api.getCustomer(customerId)
                            setSlideoutCustomer(customer ?? null)

                            const elapsedMs = Date.now() - startedAtMs
                            const remainingMs = MIN_CUSTOMER_DETAILS_SPINNER_MS - elapsedMs
                            if (remainingMs > 0) {
                                await new Promise((resolve) => window.setTimeout(resolve, remainingMs))
                            }

                            setIsCustomerSlideoutOpen(true)
                        } catch (error) {
                            console.error("Failed to load customer:", error)
                            setSlideoutCustomer(null)
                            const elapsedMs = Date.now() - startedAtMs
                            const remainingMs = MIN_CUSTOMER_DETAILS_SPINNER_MS - elapsedMs
                            if (remainingMs > 0) {
                                await new Promise((resolve) => window.setTimeout(resolve, remainingMs))
                            }
                            setIsCustomerSlideoutOpen(true)
                        } finally {
                            setIsCustomerDetailsLoading(false)
                        }
                    }}
                />
                <CustomerDetailsSlideout
                    api={api}
                    helpers={helpers}
                    customer={slideoutCustomer}
                    isOpen={isCustomerSlideoutOpen}
                    onClose={() => setIsCustomerSlideoutOpen(false)}
                    emptyMessage={
                        order?.customer_id ?
                            "Customer details could not be loaded." :
                            "No customer associated with this order."
                    }
                    onCreateOrderClick={() =>
                        navigate("/v2/order-form", {
                            state: { prefillCustomer: slideoutCustomer ?? null },
                        })
                    }
                    onEditCustomerClick={() => {
                        if (!slideoutCustomer) {
                            return
                        }
                        setShowCustomerModal(true)
                    }}
                    onCommunicationClick={() => showNotImplementedToast()}
                    onSeeAllRecentOrdersClick={() => showNotImplementedToast()}
                    {...(customerSlideoutProps ?? {})}
                />
                <CreateEditCustomerModal
                    show={showCustomerModal}
                    mode="edit"
                    customer={slideoutCustomer}
                    api={api}
                    onClose={() => setShowCustomerModal(false)}
                    onSaved={() => {
                        const id = slideoutCustomer?.id ?? null
                        if (!id || typeof api?.getCustomer !== "function") {
                            return
                        }
                        api.getCustomer(id)
                            .then((full) => setSlideoutCustomer(full?.data ?? full))
                            .catch(() => {
                                // non-blocking
                            })
                    }}
                />
                {activeTab === "order" && (
                    <OrderPage
                        app={app}
                        api={api}
                        helpers={helpers}
                        order={order}
                        manifest={manifest}
                        boxUpLines={boxUpLines}
                        boxUpBoxes={boxUpBoxes}
                        tidepoolLoading={tidepoolLoading}
                        onOrderChange={setOrder}
                        showWeighInNotes={showWeighInNotes}
                        showBoxUpTable={showBoxUpTable}
                        showShipmentNotes={showShipmentNotes}
                        showCreateSoButton={showCreateSoButton}
                        orderInformationFieldLayout={orderInformationFieldLayout}
                        lockEditsWhenConfirmed={lockEditsWhenConfirmed}
                        editLockedToastMessage={editLockedToastMessage}
                        renderOrderStatusActions={renderOrderStatusActions}
                        renderOrderFooterExtra={renderOrderFooterExtra}
                        injectBootstrap={injectBootstrap}
                        disableManifestEditWhenOffline={disableManifestEditWhenOffline}
                    />
                )}
            </div>
        </div>
    )
}
